import React from 'react';

import mixpanel from '~/components/CmsMain/mixpanel';
import { Text } from '~/components/core';
import { useCms } from '~/components/hooks/useCms';
import { MIXPANEL_EVENTS } from '~/pocs/mixpanel';
import type {
  CliveStoreInformation,
  CommunicationAttachment,
  EmailCommunication,
  PhoneCallCommunication,
} from '~/server_shared/swagger/data-contracts';
import { CONFIGURATION_FEATURES_NAMES } from '~/Types';
import { isFeatureEnabled } from '~/Utils';

import CliveRecommendationPanel from '../CliveRecommendationPanel/CliveRecommendationPanel';
import { CliveStoreProcessingStatus } from '../StatusChip/CliveStoreStatusChip';
import CliveSummaryCard from '../SummaryCard/CliveSummaryCard';
import { CliveContentType, type CliveRecommendationAvatarType } from '../types';

interface CliveAnalysisSectionProps {
  communication: PhoneCallCommunication | EmailCommunication;
  showClive: boolean;
}

/**
 * Tracks phone call copy actions
 * @param communication - Phone call communication object
 * @param contentType - Type of content being copied
 */
const trackCommunicationCopy = (
  communication: PhoneCallCommunication,
  contentType: CliveContentType.SUMMARY | CliveContentType.TRANSCRIPT
): void => {
  // Derive event name from content type
  const eventName =
    contentType === CliveContentType.SUMMARY
      ? MIXPANEL_EVENTS.PHONE_CALL_SUMMARY_COPIED
      : MIXPANEL_EVENTS.PHONE_CALL_TRANSCRIPT_COPIED;

  mixpanel.track(eventName, {
    communication_id: communication.id,
    communication_channel: communication.channel,
    content_type: contentType,
  });
};

/**
 * Tracks phone call summary viewed action
 * @param communication - Phone call communication object
 */
const trackPhoneCallSummaryViewed = (communication: PhoneCallCommunication): void => {
  mixpanel.track(MIXPANEL_EVENTS.PHONE_CALL_SUMMARY_VIEWED, {
    communication_id: communication.id,
    communication_channel: communication.channel,
    content_type: CliveContentType.SUMMARY,
  });
};

/**
 * Formats transcript text with markdown speaker formatting
 * @param transcriptText - Raw transcript text with speaker patterns
 * @returns Formatted markdown text with **Speaker:** prefixes
 */
export const formatTranscriptToMarkdown = (transcriptText: string | null | undefined): string => {
  if (!transcriptText?.trim()) return '';

  // Normalize markdown-formatted speakers: "**speaker 1:**" → "speaker 1: "
  const normalizedText = transcriptText.replace(/\*\*(\b[A-Za-z]+(?:\s+[A-Za-z0-9]+)*):\*\*\s*/g, '$1: ');

  // Pattern matches any word(s) followed by colon and space: "speaker 0: ", "John: ", "Agent Smith: "
  const processedTranscriptText = normalizedText
    .split(/(\b[A-Za-z]+(?:\s+[A-Za-z0-9]+)*):\s+/g)
    .filter(Boolean)
    .reduce((formatted, part, index, parts) => {
      // Every even index is a speaker, odd index is dialogue
      if (index % 2 === 0 && parts[index + 1]) {
        const speaker = part.trim();
        const dialogue = parts[index + 1].trim();
        return formatted + `**${speaker}:**\n${dialogue}\n\n`;
      }
      return formatted;
    }, '')
    .trim();
  return processedTranscriptText;
};

/**
 * Gets the display name for a communication channel
 * @param channel - The communication channel
 * @returns Capitalized display name ('Email' or 'Call')
 */
const getChannelDisplayName = (channel: string): string => {
  return channel === 'email' ? 'Email' : 'Call';
};

/**
 * Gets the lowercase name for a communication channel
 * @param channel - The communication channel
 * @returns Lowercase channel name ('email' or 'call')
 */
const getChannelName = (channel: string): string => {
  return channel === 'email' ? 'email' : 'call';
};

/**
 * Gets the analysis in progress title based on channel
 * @param channel - The communication channel
 * @returns Analysis title string
 */
const getAnalysisProgressTitle = (channel: string): string => {
  return `${getChannelDisplayName(channel)} analysis in progress...`;
};

/**
 * Gets the analysis in progress subtitle based on channel
 * @param channel - The communication channel
 * @returns Analysis subtitle string
 */
const getAnalysisProgressSubtitle = (channel: string): string => {
  return `I'm currently analyzing this ${getChannelName(channel)}. A detailed summary will appear here as soon as the process is complete. This may take a few minutes.`;
};

/**
 * Gets the analysis failed title based on channel
 * @param channel - The communication channel
 * @returns Error title string
 */
const getAnalysisFailedTitle = (channel: string): string => {
  return `${getChannelDisplayName(channel)} analysis failed`;
};

/**
 * Gets the analysis failed subtitle based on channel
 * @param channel - The communication channel
 * @returns Error subtitle string
 */
const getAnalysisFailedSubtitle = (channel: string): string => {
  return `I encountered an issue while trying to analyze this ${getChannelName(channel)}, please try again later or contact support if the problem persists.`;
};

/**
 * Gets the success content configuration for phone call communications
 * @param communication - Phone call communication object
 * @returns Panel content configuration for successful processing
 */
const getPhoneCallSuccessContent: (communication: PhoneCallCommunication) => CliveRecommendationStateContent = (
  communication: PhoneCallCommunication
) => {
  const noSummaryOrTranscript =
    !communication.clive_store_information?.full_summary && !communication.clive_store_information?.text;

  const subtitle = noSummaryOrTranscript
    ? 'The process is complete, but no summary or transcript is available.'
    : 'Quickly access key points and transcript.';

  return {
    title: `Clive's Overview`,
    subtitle,
    actionComponent: null,
    children: noSummaryOrTranscript ? null : (
      <div className="flex flex-col gap-8">
        {communication.clive_store_information?.full_summary && (
          <CliveSummaryCard
            title="Call Analysis"
            summaryText={communication.clive_store_information.full_summary}
            onCopySummary={() => trackCommunicationCopy(communication, CliveContentType.SUMMARY)}
          />
        )}
        {communication.clive_store_information?.text && (
          <CliveSummaryCard
            title="Call Transcript"
            summaryText={formatTranscriptToMarkdown(communication.clive_store_information?.text)}
            isCollapsible={true}
            onCopySummary={() => trackCommunicationCopy(communication, CliveContentType.TRANSCRIPT)}
          />
        )}
      </div>
    ),
    cliveAvatarType: 'happy',
  } as CliveRecommendationStateContent;
};

interface ParsedAttachmentStoreInfo {
  attachmentStoreInformation: CliveStoreInformation | null;
  wasAttachmentRejectedForIrrelevance: boolean;
}

const getAttachmetStatus: (attachment: CommunicationAttachment) => ParsedAttachmentStoreInfo = (
  attachment: CommunicationAttachment
) => {
  const attachmentStoreInformation = attachment.clive_store_information;
  const wasAttachmentSentToClive = Boolean(attachmentStoreInformation);
  const wasAttachmentProcessed =
    wasAttachmentSentToClive && attachmentStoreInformation?.processing_status === CliveStoreProcessingStatus.SUCCESS;
  const wasAttachmentUploaded = Boolean(attachment.document);
  const wasAttachmentRejectedForIrrelevance =
    wasAttachmentSentToClive && wasAttachmentProcessed && !wasAttachmentUploaded;
  return {
    attachmentStoreInformation: attachmentStoreInformation || null,
    wasAttachmentRejectedForIrrelevance,
  };
};

const getRelevantAttachments: (attachments: CommunicationAttachment[]) => CommunicationAttachment[] = (
  attachments: CommunicationAttachment[]
) => {
  return attachments.filter((attachment) => !getAttachmetStatus(attachment).wasAttachmentRejectedForIrrelevance);
};

/**
 * Gets the success content configuration for email communications
 * @param communication - Email communication object
 * @returns Panel content configuration for successful processing
 */
const getEmailSuccessContent: (communication: EmailCommunication) => CliveRecommendationStateContent = (
  communication: EmailCommunication
) => {
  const { clive_store_information: cliveInfo } = communication;
  const attachments = communication.attachments || [];

  const processedAttachments = attachments.filter((attachment) => Boolean(attachment.clive_store_information));
  const processingAttachments = processedAttachments.filter(
    (attachment) => attachment.clive_store_information?.processing_status !== CliveStoreProcessingStatus.SUCCESS
  );

  const relevantAttachments = getRelevantAttachments(attachments);

  const noContent = !cliveInfo?.full_summary && (attachments.length === 0 || processingAttachments.length > 0);

  const attachmentCountText = attachments.length === 1 ? '1 attachment' : `${attachments.length} attachments`;
  const processingAttachmentsCountText =
    processingAttachments.length === 1 ? '1 attachment' : `${processingAttachments.length} attachments`;
  const processingText =
    processingAttachments.length > 0 ? `${processingAttachmentsCountText} are still being processed.` : '';

  const attachmentText = `This email contains ${attachmentCountText}. ${processingText}`;

  const preface = cliveInfo?.short_description
    ? `${cliveInfo.short_description} ${attachmentText}`
    : `${attachmentText}`;

  return {
    title: `Clive's Overview`,
    subtitle: noContent
      ? 'The process is complete, but no summary is available.'
      : 'Quickly access key points of email and attachments',
    actionComponent: null,
    children: noContent ? null : (
      <div className="flex flex-col gap-8">
        {/* Email Preface */}
        <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.REGULAR}>
          {preface}
        </Text>
        {/* Email Body Summary */}
        {cliveInfo?.full_summary && (
          <CliveSummaryCard
            title="Email Body Summary"
            summaryText={cliveInfo.full_summary}
            isCollapsible={true}
            isOpenByDefault={true}
            summaryStatus={cliveInfo.processing_status as CliveStoreProcessingStatus}
          />
        )}
        {/* Email Attachments Summaries */}
        {relevantAttachments.map((attachment, index) => {
          const { wasAttachmentRejectedForIrrelevance, attachmentStoreInformation } = getAttachmetStatus(attachment);

          if (!attachmentStoreInformation || wasAttachmentRejectedForIrrelevance) {
            return null;
          }

          const summary = attachmentStoreInformation?.full_summary;
          const attachmentName = attachment.document?.document_name || attachment.file_name;
          const attachmentTitle = `Attachment ${index + 1}: ${attachmentName}`;

          return (
            <CliveSummaryCard
              key={attachment.id || index}
              title={attachmentTitle}
              summaryText={summary || ''}
              isCollapsible={true}
              summaryStatus={attachmentStoreInformation?.processing_status as CliveStoreProcessingStatus}
            />
          );
        })}
      </div>
    ),
    cliveAvatarType: 'happy',
  } as CliveRecommendationStateContent;
};

/**
 * Gets the success content configuration based on communication type
 * @param communication - Communication object
 * @returns Panel content configuration for successful processing
 */
const getSuccessContent: (
  communication: PhoneCallCommunication | EmailCommunication
) => CliveRecommendationStateContent | null = (communication: PhoneCallCommunication | EmailCommunication) => {
  if (communication.channel === 'phone_call') {
    return getPhoneCallSuccessContent(communication);
  }

  if (communication.channel === 'email') {
    return getEmailSuccessContent(communication);
  }

  // Fallback for unknown channel types
  return null;
};

interface CliveRecommendationStateContent {
  title: string;
  subtitle: string;
  actionComponent: React.ReactNode;
  children: React.ReactNode;
  cliveAvatarType: CliveRecommendationAvatarType;
}

/**
 * Gets the content configuration for Clive recommendation panel
 * @param communication - Communication object
 * @returns Panel content configuration or null if not applicable
 */
export const getCliveRecommendationStateContent: (
  communication: PhoneCallCommunication | EmailCommunication
) => CliveRecommendationStateContent | null = (communication: PhoneCallCommunication | EmailCommunication) => {
  const { clive_store_information: cliveStoreInformation } = communication;
  if (!cliveStoreInformation) {
    return null;
  }

  const { processing_status } = cliveStoreInformation;

  if (communication.channel === 'phone_call' && !(communication as PhoneCallCommunication).recording_stored_file_id) {
    return {
      title: 'No call recording available',
      subtitle: `This call wasn't recorded, so I couldn't generate a summary or analysis. To enable insights for future calls, make sure call recording is turned on.`,
      actionComponent: null,
      children: null,
      cliveAvatarType: 'happy',
    } as CliveRecommendationStateContent;
  }
  if (processing_status === CliveStoreProcessingStatus.PROCESSING) {
    return {
      title: getAnalysisProgressTitle(communication.channel),
      subtitle: getAnalysisProgressSubtitle(communication.channel),
      actionComponent: null,
      children: null,
      cliveAvatarType: 'loading',
    } as CliveRecommendationStateContent;
  }
  if (processing_status === CliveStoreProcessingStatus.ERROR) {
    return {
      title: getAnalysisFailedTitle(communication.channel),
      subtitle: getAnalysisFailedSubtitle(communication.channel),
      actionComponent: null,
      children: null,
      cliveAvatarType: 'error',
    } as CliveRecommendationStateContent;
  }
  if (processing_status === CliveStoreProcessingStatus.SUCCESS) {
    return getSuccessContent(communication);
  }

  return null;
};

const CliveAnalysisSection: React.FC<CliveAnalysisSectionProps> = ({ communication, showClive }) => {
  const { userOrganization } = useCms();

  if (!showClive) {
    return null;
  }

  // Check channel support: email requires feature flag, phone_call is always supported when AI is enabled
  if (communication.channel === 'email') {
    const isChannelSupported = isFeatureEnabled(userOrganization, CONFIGURATION_FEATURES_NAMES.CLIVE_EMAIL_SUMMARY);
    if (!isChannelSupported) {
      return null;
    }
  } else if (communication.channel !== 'phone_call') {
    // Unsupported channel type
    return null;
  }

  const cliveRecommendationContent = getCliveRecommendationStateContent(communication);
  if (!cliveRecommendationContent) {
    return null;
  }

  const { title: cliveTitle, subtitle, actionComponent, children } = cliveRecommendationContent;
  const hasCliveRecommendationContent = cliveTitle || subtitle || actionComponent || children;

  if (!hasCliveRecommendationContent) {
    return null;
  }

  const handlePanelExpand =
    communication.channel === 'phone_call'
      ? () => trackPhoneCallSummaryViewed(communication as PhoneCallCommunication)
      : undefined;

  return (
    <CliveRecommendationPanel
      title={cliveTitle}
      subtitle={subtitle}
      actionComponent={actionComponent}
      onExpand={handlePanelExpand}
    >
      {children}
    </CliveRecommendationPanel>
  );
};

export default CliveAnalysisSection;
